import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  TextField,
  IconButton,
  Typography,
  LinearProgress,
  Avatar,
  Paper,
  Fade,
  Chip,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import researchService from '../../services/researchService';

/**
 * Chat interface component for the research interview.
 * Uses MUI components for a polished, professional look.
 */
const InterviewChat = ({ sessionId, surveyInfo, initialMessage, onComplete }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({
    percentage: 0,
    phases_completed: 0,
    total_phases: surveyInfo?.phases_count || 4,
    current_phase: ''
  });
  const messagesEndRef = useRef(null);

  // Initialize with the first message from the agent
  useEffect(() => {
    if (initialMessage) {
      setMessages([{
        role: 'assistant',
        content: initialMessage,
        timestamp: new Date()
      }]);
    }
  }, [initialMessage]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    // Optimistically add user message
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await researchService.sendMessage(sessionId, userMessage.content);

      // Add AI response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        phase: response.current_phase
      }]);

      // Update progress
      if (response.progress) {
        setProgress({
          percentage: response.progress.percentage || 0,
          phases_completed: response.progress.phases_completed || 0,
          total_phases: response.progress.total_phases || surveyInfo?.phases_count || 4,
          current_phase: response.current_phase || ''
        });
      }

      // Check if interview is complete
      if (response.is_completed) {
        setTimeout(() => onComplete(), 2000);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an issue. Could you please repeat that?',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Card
      sx={{
        height: 'calc(100vh - 48px)',
        maxWidth: 800,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
      }}
    >
      {/* Header with progress */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 2.5
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="h6" fontWeight="bold">
            {surveyInfo?.title || 'Research Interview'}
          </Typography>
          {progress.current_phase && (
            <Chip
              label={progress.current_phase.replace(/_/g, ' ')}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                textTransform: 'capitalize'
              }}
            />
          )}
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress.percentage}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'white',
              borderRadius: 3
            }
          }}
        />

        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {progress.phases_completed} of {progress.total_phases} topics covered
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {progress.percentage}% complete
          </Typography>
        </Box>
      </Box>

      {/* Messages area */}
      <CardContent
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 3,
          bgcolor: 'grey.50',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {messages.map((message, index) => (
          <Fade in key={index} timeout={300}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  maxWidth: '80%',
                  gap: 1.5
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main',
                    width: 38,
                    height: 38,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                >
                  {message.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                </Avatar>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: message.role === 'user'
                      ? 'primary.main'
                      : message.isError
                        ? 'error.light'
                        : 'white',
                    color: message.role === 'user' || message.isError ? 'white' : 'text.primary',
                    borderRadius: 3,
                    borderTopLeftRadius: message.role === 'user' ? 12 : 4,
                    borderTopRightRadius: message.role === 'user' ? 4 : 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      '& p': { margin: 0 }
                    }}
                  >
                    {message.content}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          </Fade>
        ))}

        {/* Typing indicator */}
        {loading && (
          <Fade in>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'secondary.main', width: 38, height: 38 }}>
                <SmartToyIcon />
              </Avatar>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 3,
                  borderTopLeftRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CircularProgress size={16} thickness={6} />
                <Typography variant="body2" color="text.secondary">
                  Thinking...
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}

        <div ref={messagesEndRef} />
      </CardContent>

      {/* Input area */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box display="flex" gap={1.5} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your response..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: 'grey.50'
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500'
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          Press Enter to send, Shift+Enter for new line
        </Typography>
      </Box>
    </Card>
  );
};

export default InterviewChat;
